import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cs from 'classnames';
import { debounce } from 'lodash';

import { camelCaseToReadableFormat, ia, iaRa } from 'lib/helpers/utility';
import useDebounce from 'lib/hooks/useDebounce';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';

import NotFound from 'components/practice/NewAppointment/components/NotFound';

import Icon from '../Icon/Icon';
import Input from '../Input/Input';
import Loader from '../Loader/Loader';

const LIMIT_DEFAULT = 25;
const INITIAL_PAGE = 1;
const SCROLL_THRESHOLD = 30; // pixels before end to trigger next page fetch

const NewInfiniteScroll = ({
  queryKey,
  endpoint,
  queryProps = {},
  children, // children is expected to be a function
  EmptyComponent = NotFound,
  className = '',
  withSearch = false,
  formatData,
  setData,
  data,
  BeforeComponent = () => null,
  queryFn,
  inputWrapperClassName,
  showGroupLabel = false,
  values = [],
  isOptions = false,
  placeholder = 'Search',
  dependencies = [],
  isFocused = false,
  minHeight = 0,
  minWidth = 0
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const debounceSearch = useDebounce(searchTerm, 300);

  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const name = camelCaseToReadableFormat(queryKey);

  const focusInput = () => {
    if (inputRef?.current) {
      inputRef.current.focus();
    }
  };

  const {
    data: newData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage
  } = useInfiniteFetchData({
    queryKey,
    url: endpoint,
    queryFn,
    params: {
      limit: LIMIT_DEFAULT,
      page: INITIAL_PAGE,
      withCount: true,
      ...queryProps,
      filters: { searchTerm: debounceSearch }
    },
    dependencies: ['infinite', debounceSearch, ...dependencies],
    formatData,
    isOptions,
    onSuccess: (data) => {
      focusInput();
      if (data?.pages?.length && setData) {
        const flattenedData = data?.pages?.flatMap((page) => page?.[queryKey]) || [];
        setData(flattenedData || []);
      }
    }
  });

  const handleScroll = useCallback(() => {
    const div = scrollRef.current;
    if (!div) return;

    const isNearBottom = div.scrollTop + div.clientHeight >= div.scrollHeight - SCROLL_THRESHOLD;

    if (isNearBottom && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  useEffect(() => {
    const div = scrollRef.current;
    if (!div) return;

    const debounced = debounce(handleScroll, 200);
    div.addEventListener('scroll', debounced);

    return () => {
      div.removeEventListener('scroll', debounced);
      debounced.cancel();
    };
  }, [handleScroll]);

  const handleInputChange = (event) => {
    const newSearchTerm = event.target.value;
    setSearchTerm(newSearchTerm);
  };

  useEffect(() => {
    if (isFocused) {
      focusInput();
    }
  }, [data, isFocused]);

  const unsavedData = useMemo(() => {
    const newDataArray = iaRa(newData);
    const valuesArray = iaRa(values);

    return newDataArray
      .filter((item) => !valuesArray.some((_item) => _item?.value === item?.value))
      .filter(Boolean);
  }, [newData, values]);

  return (
    <div
      className="relative flex flex-1 flex-col gap-2 overflow-hidden pb-2"
      style={{ minHeight, minWidth }}>
      <BeforeComponent />
      {withSearch && (
        <div className="sticky top-0 z-10 w-full bg-white">
          <Input
            forwardedRef={inputRef}
            rounded="none"
            name="searchTerm"
            value={searchTerm}
            id={`search-${placeholder}`}
            data-qa={`search-${placeholder}`}
            placeholder={placeholder}
            rightIcon={searchTerm && 'close-linear'}
            rightIconClick={() => setSearchTerm('')}
            inputWrapperClassName={cs(
              'mt-0 border-x-0 border-t-0 border-b border-b-neutral-300 border-solid',
              inputWrapperClassName
            )}
            onChange={handleInputChange}
          />
          {showGroupLabel && <p className="text-xs font-500 leading-5 text-neutral-500">{name}</p>}
        </div>
      )}
      {isLoading ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 overflow-y-auto py-5">
          <Loader size={32} color="primary" />
        </div>
      ) : ia(unsavedData) || ia(values) ? (
        <div
          ref={scrollRef}
          className={cs('flex flex-1 flex-col gap-2 overflow-y-auto', className)}>
          {values.length > 0 && (
            <div className="flex flex-col gap-2 border-0 border-b border-solid border-neutral-300">
              {values.map((item, index) => (
                <React.Fragment key={item.value}>{children({ item, index })}</React.Fragment>
              ))}
            </div>
          )}
          {unsavedData?.map((item, index) => (
            <React.Fragment key={index}>{children({ item, index })}</React.Fragment>
          ))}
        </div>
      ) : (
        <EmptyComponent />
      )}
      {isFetchingNextPage && (
        <div className="align-center sticky bottom-0 flex justify-center pb-2">
          <Icon icon="loader-linear" className="animate-spin" color="primary" size={24} />
        </div>
      )}
    </div>
  );
};
export default NewInfiniteScroll;
